/**
 * App - Main application component
 * Sets up routing, authentication context, and global providers
 * Implements lazy loading for route components and proper error boundaries
 */

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/layout/ErrorBoundary';
import ScrollToTop from './components/ui/ScrollToTop';
import ScrollManager from './components/ui/ScrollManager';
import CreatePromptFAB from './components/ui/CreatePromptFAB';
import { queryClient } from './lib/react-query';
import config from './config';
import { analyticsService } from './services/analyticsService';
import { usePWAInit } from './hooks/usePWA';
import InstallPrompt from './components/pwa/InstallPrompt';
import OfflineIndicator from './components/pwa/OfflineIndicator';
import UpdatePrompt from './components/pwa/UpdatePrompt';
import VersionChecker from './components/layout/VersionChecker';
import { logger } from './utils/logger';


// Core pages - loaded immediately for better UX
import Home from './pages/Home';
import Auth from './pages/Auth';
import NotFound from './pages/NotFound';
import ServerError from './pages/ServerError';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import BrowsePrompts from './pages/BrowsePrompts';


// Lazy load other pages for code splitting
const PromptDetail = React.lazy(() => import('./pages/PromptDetail'));
const CreatePrompt = React.lazy(() => import('./pages/CreatePrompt'));
// const Profile = React.lazy(() => import('./pages/Profile'));
const SavedPrompts = React.lazy(() => import('./pages/SavedPrompts'));
const MyPrompts = React.lazy(() => import('./pages/MyPrompts'));

// Lazy load admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const PromptModeration = React.lazy(() => import('./pages/admin/PromptModeration'));
const ReportManagement = React.lazy(() => import('./pages/admin/ReportManagement'));
const CategoryManagement = React.lazy(() => import('./pages/admin/CategoryManagement'));
const UserInteractions = React.lazy(() => import('./pages/admin/UserInteractions'));
const SystemManagement = React.lazy(() => import('./pages/admin/SystemManagement'));
const TagManagement = React.lazy(() => import('./pages/admin/TagManagement'));
const PremiumPhotos = React.lazy(() => import('./pages/admin/PremiumPhotos'));
const BulkUploadQueue = React.lazy(() => import('./pages/admin/BulkUploadQueue'));
const PremiumVideos = React.lazy(() => import('./pages/admin/PremiumVideos'));
const ChatManagement = React.lazy(() => import('./pages/admin/ChatManagement'));
const Chat = React.lazy(() => import('./pages/Chat'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  // Initialize PWA
  const { isInitialized, initError } = usePWAInit();

  // Initialize analytics and error monitoring
  React.useEffect(() => {
    // Initialize services
    if (config.FEATURES.ENABLE_ANALYTICS) {
      logger.child('APP').log('📊 Analytics initialized');
    }

    if (config.FEATURES.ENABLE_ERROR_REPORTING) {
      logger.child('APP').log('🚨 Error monitoring initialized');
    }

    // Track initial page view
    analyticsService.trackPageView(window.location.pathname, document.title);
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <VersionChecker />
          <Router>
            <Layout>
              {/* Global Floating Action Buttons - Inside Router */}
              <CreatePromptFAB />
              <ScrollManager />
              <ScrollToTop />

              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/login" element={<Auth />} />
                  <Route path="/register" element={<Auth />} />
                  <Route path="/prompt/:id" element={<PromptDetail />} />
                  {/* SEO-friendly slug route — same component, reads :slug param */}
                  <Route path="/p/:slug" element={<PromptDetail />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />

                  <Route
                    path="/prompts"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <BrowsePrompts />
                      </Suspense>
                    }
                  />

                  {/* Protected Routes */}
                  <Route
                    path="/prompts/create"
                    element={
                      <ProtectedRoute redirectTo="/login">
                        <CreatePrompt />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/my-prompts"
                    element={
                      <ProtectedRoute redirectTo="/login">
                        <MyPrompts />
                      </ProtectedRoute>
                    }
                  />

                  {/* <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute redirectTo="/login">
                      <Profile />
                    </ProtectedRoute>
                  } 
                /> */}

                  <Route
                    path="/saved"
                    element={
                      <ProtectedRoute redirectTo="/login">
                        <SavedPrompts />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute redirectTo="/login">
                        <Chat />
                      </ProtectedRoute>
                    }
                  />

                  {/* Future routes for Phase 2/3 */}
                  {/* <Route 
                  path="/prompts" 
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                          <h1 className="text-2xl font-bold text-gray-900 mb-4">Browse Prompts</h1>
                          <p className="text-gray-600">Coming in Phase 3 - Advanced search and filtering</p>
                        </div>
                      </div>
                    </Suspense>
                  } 
                />

                <Route 
                  path="/search" 
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                          <h1 className="text-2xl font-bold text-gray-900 mb-4">Search Results</h1>
                          <p className="text-gray-600">Coming in Phase 3 - Advanced search functionality</p>
                        </div>
                      </div>
                    </Suspense>
                  } 
                />
                 */}

                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <UserManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/prompts"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <PromptModeration />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/reports"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <ReportManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <CategoryManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/interactions"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <UserInteractions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/system"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <SystemManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tags"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <TagManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/premium-photos"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <PremiumPhotos />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/premium-videos"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <PremiumVideos />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/bulk-upload"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <BulkUploadQueue />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/chat"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <ChatManagement />
                      </ProtectedRoute>
                    }
                  />

                  {/* Contact/Help Page */}
                  <Route path="/contact" element={<Contact />} />

                  {/* Error Routes */}
                  <Route path="/error" element={<ServerError />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
          </Router>

          {/* React Query Devtools - only in development */}
          {config.isDevelopment() && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </AuthProvider>
      </QueryClientProvider>

      {/* PWA Components */}
      {isInitialized && (
        <>
          <InstallPrompt showAsBanner={true} />
          <UpdatePrompt position="bottom" />
          <OfflineIndicator position="top" showDetails={false} />
        </>
      )}

      {initError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 max-w-sm">
          <p className="text-sm text-red-800">PWA initialization failed: {initError}</p>
        </div>
      )}
    </ErrorBoundary>
  );
}

export default App;
